import { useState, useCallback, useRef, useLayoutEffect } from 'react';

interface UseResizableOptions {
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  /** Which edge the drag handle sits on — determines drag direction */
  side: 'left' | 'right';
  /** localStorage key to persist the width */
  storageKey?: string;
  /** CSS custom property name to set on document.documentElement (e.g. '--ask-sidebar-width') */
  cssVar?: string;
  /** Value for the CSS var when the panel is inactive/hidden. Defaults to '0px'. */
  cssVarInactive?: string;
  /** Whether the panel is currently active/visible */
  active?: boolean;
}

export function useResizable({
  defaultWidth,
  minWidth,
  maxWidth,
  side,
  storageKey,
  cssVar,
  cssVarInactive = '0px',
  active = true,
}: UseResizableOptions) {
  const [width, setWidth] = useState(() => {
    if (storageKey) {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsed = parseInt(stored, 10);
          if (!isNaN(parsed)) return Math.min(maxWidth, Math.max(minWidth, parsed));
        }
      } catch { /* noop */ }
    }
    return defaultWidth;
  });

  // Re-clamp if minWidth/maxWidth change after initialisation
  const clampedWidth = Math.min(maxWidth, Math.max(minWidth, width));
  if (clampedWidth !== width) setWidth(clampedWidth);

  const widthRef = useRef(clampedWidth);
  widthRef.current = clampedWidth;

  // Set the CSS custom property so Layout can use it for margin calculation
  useLayoutEffect(() => {
    if (!cssVar) return;
    document.documentElement.style.setProperty(
      cssVar,
      active ? `${clampedWidth}px` : cssVarInactive,
    );
    return () => {
      document.documentElement.style.setProperty(cssVar, '0px');
    };
  }, [cssVar, cssVarInactive, active, clampedWidth]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = widthRef.current;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.documentElement.dataset.resizing = '1';

    const onMouseMove = (ev: MouseEvent) => {
      // Dragging left handle leftward = panel gets wider
      const delta = side === 'left'
        ? startX - ev.clientX
        : ev.clientX - startX;
      const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidth + delta));
      setWidth(newWidth);
    };

    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      delete document.documentElement.dataset.resizing;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      if (storageKey) {
        try { localStorage.setItem(storageKey, String(widthRef.current)); } catch { /* noop */ }
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [side, minWidth, maxWidth, storageKey]);

  return { width: clampedWidth, setWidth, handleMouseDown, minWidth, maxWidth };
}
